import React, { useState, useEffect } from "react";
import styles from "./GenerateQuestions.module.css";
import Sidebar from "../sidenav/Sidenav";
import { Link } from "react-router-dom";
import { ReactComponent as Hemberger } from "../assets/icons/Hemberger.svg";
import { ReactComponent as UserIcon } from "../assets/icons/UserIcon.svg";
import isDesktop from "../isDesktop";
import GeminiChat from "../Components/GeminiChat/GeminiChat";
import { hasStoredTabAccess } from '../utils/access';

const GenerateQuestions = () => {
  const isDesktopDevice = isDesktop();
  const isAuthorized = hasStoredTabAccess('Generate Questions');
  const [topic, setTopic] = useState("");
  const [types, setTypes] = useState("");
  const [examples, setExamples] = useState("");
  const [moreRefs, setMoreRefs] = useState("");
  const [extraNotes, setExtraNotes] = useState("");
  const [output, setOutput] = useState("");
  const [isDark, setIsDark] = useState(false);
  const [copyStatus, setCopyStatus] = useState("Copy Prompt");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  useEffect(() => {
    const savedTheme = localStorage.getItem("theme");
    if (savedTheme === "dark") {
      setIsDark(true);
      document.body.setAttribute("data-theme", "dark");
    }
  }, []);

  const toggleTheme = () => {
    setIsDark(!isDark);
    if (!isDark) {
      document.body.setAttribute("data-theme", "dark");
      localStorage.setItem("theme", "dark");
    } else {
      document.body.removeAttribute("data-theme");
      localStorage.setItem("theme", "light");
    }
  };

  const generatePrompt = () => {
    const prompt = `
You are a Python expert helping build question generation scripts for math problems.

I already have a fully working ideal Python script (included below) that generates algebra-based multiple-choice questions in CSV format using structured logic and formatting.

Now I want to create a **new script for a different math topic** using the same structure and methodology.

---

## 🧩 TASK OBJECTIVE:

Write a new Python script that generates MCQ-based questions (and saves them to a CSV) **for a different math topic**, using the **same design, architecture, structure, output format, and logic** as the ideal script provided below.

Ensure the new script matches:

- Same input structure (number of questions per type)
- Same output format (CSV with \`question\`, \`answer\`, \`option_A\`, \`option_B\`, \`option_C\`, \`option_D\`)
- Uses ASCII math formatting (via \`asciimath\` or formatted as plain ASCII expressions)
- Questions and options must follow the **instruction style**, **content rules**, and **distractor logic** that I provide below.

---

## 📘 Ideal Script (Reference)

class StatisticsProblemGenerator:
    def __init__(self):
        self.variable_symbols = ['p', 'x', 'k', 'a', 'b', 'n', 'm', 'f']
        self.question_templates = [
            "The mean of the following data is {mean}. Find the value of {var}. (Round your answer to 1 decimal place)",
            "If the mean of the following data is {mean}, find the value of {var}. (Round your answer to 1 decimal place)",
            "Find the value of {var} for the following distribution whose mean is {mean}. (Round your answer to 1 decimal place)",
            "Find the missing value of {var} for the following distribution whose mean is {mean}. (Round your answer to 1 decimal place)",
            "Find the missing frequency ({var}) for the following distribution whose mean is {mean}. (Round your answer to 1 decimal place)",
            "Find the value of {var}, if the mean of the following distribution is {mean}. (Round your answer to 1 decimal place)",
            "The arithmetic mean of the following data is {mean}. Find the value of {var}. (Round your answer to 1 decimal place)",
            "If the mean of the following distribution is {mean}, find the value of {var}. (Round your answer to 1 decimal place)",
            "Find out the missing item {var} of the following distribution, where arithmetic mean {mean}. (Round your answer to 1 decimal place)",
            "The mean of the following distribution is {mean}. Find the value of {var}. (Round your answer to 1 decimal place)",
            "Find the value of {var} if the arithmetic mean is {mean}. (Round your answer to 1 decimal place)"
        ]
        
        # Templates for two missing frequencies
        self.two_missing_templates = [
            "The mean of the following distribution is {mean} and the total frequency is {total}. Find the value of {var}. (Round your answer to 1 decimal place)",
            "If the mean is {mean} and total frequency is {total}, find the missing frequency {var}. (Round your answer to 1 decimal place)",
            "Find the missing frequencies in the following frequency distribution if the mean is {mean} and total frequency is {total}. (Round your answer to 1 decimal place)",
            "The arithmetic mean is {mean} and sum of frequencies is {total}. Find the value of {var}. (Round your answer to 1 decimal place)"
        ]
        
        self.generated_problems = set()  # Track generated problems to avoid duplicates
        
    def generate_single_missing_value_table(self):
        """Generate table with one missing x-value"""
        n_entries = random.randint(5, 8)
        var_symbol = random.choice(['p', 'x', 'k'])
        
        # Generate x values (sorted)
        x_values = sorted(random.sample(range(1, 101), n_entries))
        
        # Generate frequencies
        frequencies = [random.randint(2, 20) for _ in range(n_entries)]
        
        # Choose which x-value to make missing
        missing_index = random.randint(0, n_entries - 1)
        actual_missing = x_values[missing_index]
        x_values[missing_index] = var_symbol
        
        return x_values, frequencies, var_symbol, actual_missing, 'x', missing_index
    
    def generate_single_missing_frequency_table(self):
        """Generate table with one missing frequency"""
        n_entries = random.randint(5, 8)
        var_symbol = random.choice(['p', 'f', 'k'])
        
        # Generate x values (sorted)
        x_values = sorted(random.sample(range(1, 101), n_entries))
        
        # Generate frequencies
        frequencies = [random.randint(2, 20) for _ in range(n_entries)]
        
        # Choose which frequency to make missing
        missing_index = random.randint(0, n_entries - 1)
        actual_missing = frequencies[missing_index]
        frequencies[missing_index] = var_symbol
        
        return x_values, frequencies, var_symbol, actual_missing, 'f', missing_index
    
    def generate_two_missing_frequencies_table(self):
        """Generate table with two missing frequencies (same variable) with total given"""
        n_entries = random.randint(6, 8)
        var_symbol = random.choice(['p', 'f', 'k'])
        
        # Generate x values (sorted)
        x_values = sorted(random.sample(range(1, 101), n_entries))
        
        # Generate frequencies
        frequencies = [random.randint(2, 15) for _ in range(n_entries)]
        
        # Choose two positions for missing frequencies
        missing_indices = random.sample(range(n_entries), 2)
        actual_missing_values = [frequencies[i] for i in missing_indices]
        
        # Both missing frequencies will have the same value
        actual_missing_value = random.randint(3, 12)
        for idx in missing_indices:
            frequencies[idx] = actual_missing_value
        
        # Calculate total frequency
        total_frequency = sum(frequencies)
        
        # Replace with variable after calculating total
        for idx in missing_indices:
            frequencies[idx] = var_symbol
        
        return x_values, frequencies, var_symbol, actual_missing_value, total_frequency, missing_indices
    
    def generate_two_different_missing_frequencies_table(self):
        """Generate table with two different missing frequencies (p and q) with total given"""
        n_entries = random.randint(6, 8)
        var_symbols = ['p', 'q']
        
        # Generate x values (sorted)
        x_values = sorted(random.sample(range(1, 101), n_entries))
        
        # Generate frequencies
        frequencies = [random.randint(2, 15) for _ in range(n_entries)]
        
        # Choose two positions for missing frequencies
        missing_indices = random.sample(range(n_entries), 2)
        actual_missing_values = [random.randint(3, 12) for _ in range(2)]
        
        # Set actual values
        for i, idx in enumerate(missing_indices):
            frequencies[idx] = actual_missing_values[i]
        
        # Calculate total frequency
        total_frequency = sum(frequencies)
        
        # Replace with variables after calculating total
        for i, idx in enumerate(missing_indices):
            frequencies[idx] = var_symbols[i]
        
        return x_values, frequencies, var_symbols, actual_missing_values, total_frequency, missing_indices
    
    def generate_algebraic_expressions_table(self):
        """Generate table with algebraic expressions"""
        n_entries = 5
        var_symbol = random.choice(['a', 'p', 'k'])
        
        # Generate x values (sorted)
        x_values = sorted(random.sample(range(10, 101, 10), n_entries))
        
        # Generate algebraic expressions for frequencies
        frequencies = []
        coefficients = []
        constants = []
        
        # 2-3 algebraic expressions
        num_algebraic = random.randint(2, 3)
        algebraic_positions = random.sample(range(n_entries), num_algebraic)
        
        for i in range(n_entries):
            if i in algebraic_positions:
                coeff = random.randint(1, 3)  # Keep coefficients small
                const = random.randint(2, 10)  # Keep constants positive and reasonable
                
                # Create simple expressions
                if random.choice([True, False]) and const > coeff:
                    # Subtraction: like "2a-1" but ensure result stays positive
                    const = random.randint(1, coeff)
                    expr = f"{coeff}{var_symbol}-{const}"
                    const = -const
                else:
                    # Addition: like "2a+3"
                    expr = f"{coeff}{var_symbol}+{const}"
                
                frequencies.append(expr)
                coefficients.append(coeff)
                constants.append(const)
            else:
                freq = random.randint(5, 20)
                frequencies.append(freq)
                coefficients.append(0)
                constants.append(freq)
        
        return x_values, frequencies, var_symbol, coefficients, constants
    
    def solve_for_missing_value(self, x_values, frequencies, var_symbol, missing_info, target_mean, total_freq=None):
        """Solve for missing value using symbolic computation"""
        try:
            # Use sympy for accurate symbolic computation
            var = symbols(var_symbol)
            
            # Calculate sum of fx and sum of f symbolically
            sum_fx = 0
            sum_f = 0
            
            for i, (x_val, f_val) in enumerate(zip(x_values, frequencies)):
                # Handle x values
                if isinstance(x_val, str) and x_val == var_symbol:
                    x_sym = var
                else:
                    x_sym = x_val
                
                # Handle frequency values
                if isinstance(f_val, str):
                    if var_symbol in str(f_val):
                        # Parse algebraic expression
                        f_sym = self.parse_expression(f_val, var)
                    else:
                        f_sym = var
                else:
                    f_sym = f_val
                
                sum_fx += x_sym * f_sym
                sum_f += f_sym
            
            # For two missing frequencies with total given, add constraint
            if total_freq is not None:
                # sum_f should equal total_freq
                freq_equation = Eq(sum_f, total_freq)
                solutions_freq = solve(freq_equation, var)
                if solutions_freq:
                    return round(float(solutions_freq[0].evalf()), 1)
            
            # Set up equation: sum_fx / sum_f = target_mean
            # Rearrange to: sum_fx = target_mean * sum_f
            equation = Eq(sum_fx, target_mean * sum_f)
            
            # Solve the equation
            solutions = solve(equation, var)
            
            if solutions:
                # Take the first real solution
                for sol in solutions:
                    try:
                        solution_float = float(sol.evalf())
                        if solution_float > 0:  # Ensure positive solution
                            return round(solution_float, 1)
                    except:
                        continue
                        
                # If no positive solution found, try the first one anyway
                try:
                    solution_float = float(solutions[0].evalf())
                    return round(solution_float, 1)
                except:
                    return None
            else:
                return None
                
        except Exception as e:
            print(f"Error solving equation: {e}")
            return None
    
    def solve_two_different_frequencies(self, x_values, frequencies, var_symbols, missing_indices, target_mean, total_freq):
        """Solve for two different missing frequencies (p and q)"""
        try:
            from sympy import symbols, Eq, solve
            p, q = symbols('p q')
            
            # Create frequency list with symbols
            freq_with_symbols = frequencies.copy()
            freq_with_symbols[missing_indices[0]] = p
            freq_with_symbols[missing_indices[1]] = q
            
            # Calculate sum of fx and sum of f
            sum_fx = sum(x * f for x, f in zip(x_values, freq_with_symbols))
            sum_f = sum(freq_with_symbols)
            
            # Set up equations
            eq1 = Eq(sum_f, total_freq)  # Total frequency constraint
            eq2 = Eq(sum_fx, target_mean * sum_f)  # Mean constraint
            
            # Solve system of equations
            solutions = solve([eq1, eq2], [p, q])
            
            if solutions:
                p_val = float(solutions[p].evalf())
                q_val = float(solutions[q].evalf())
                
                if p_val > 0 and q_val > 0:
                    return [round(p_val, 1), round(q_val, 1)]
            
            return None
            
        except Exception as e:
            print(f"Error solving two frequencies: {e}")
            return None
    
    def parse_expression(self, expr_str, var):
        """Parse algebraic expression string into sympy expression"""
        try:
            # Handle simple cases first
            expr_str = str(expr_str).strip()
            
            # Case 1: Just the variable (e.g., "p")
            if expr_str == str(var):
                return var
            
            # Case 2: Coefficient + variable (e.g., "2p", "3k")
            if expr_str.replace(str(var), "").replace("+", "").replace("-", "").isdigit():
                if "+" in expr_str:
                    parts = expr_str.split("+")
                    coeff_part = parts[0].replace(str(var), "")
                    coeff = int(coeff_part) if coeff_part else 1
                    const = int(parts[1])
                    return coeff * var + const
                elif "-" in expr_str and not expr_str.startswith("-"):
                    parts = expr_str.split("-")
                    coeff_part = parts[0].replace(str(var), "")
                    coeff = int(coeff_part) if coeff_part else 1
                    const = -int(parts[1])
                    return coeff * var + const
                else:
                    # Just coefficient and variable
                    coeff_str = expr_str.replace(str(var), "")
                    if coeff_str == "" or coeff_str == "+":
                        coeff = 1
                    elif coeff_str == "-":
                        coeff = -1
                    else:
                        coeff = int(coeff_str)
                    return coeff * var
            
            # Case 3: More complex expressions
            # Replace variable with *var for multiplication
            temp_expr = expr_str
            for i in range(10):  # Handle coefficients 0-9
                temp_expr = temp_expr.replace(f"{i}{var}", f"{i}*{var}")
            
            # Handle standalone variable
            import re
            temp_expr = re.sub(f"(?<![0-9]){var}(?![0-9])", f"1*{var}", temp_expr)
            
            # Use sympy to parse
            from sympy import sympify
            return sympify(temp_expr.replace(str(var), f"*{var}")).subs(f"*{var}", var)
            
        except Exception as e:
            # Ultimate fallback - assume it's coefficient * var
            try:
                coeff_str = expr_str.replace(str(var), "")
                if not coeff_str or coeff_str == "+":
                    return var
                elif coeff_str == "-":
                    return -var
                else:
                    return int(coeff_str) * var
            except:
                return var
    
    def calculate_target_mean_from_actual(self, x_values, frequencies, var_symbol, actual_values, missing_type, missing_indices):
        """Calculate what the mean should be given actual values - ALWAYS return 1 decimal place"""
        try:
            # Create numerical version with actual values
            x_numeric = x_values.copy()
            f_numeric = frequencies.copy()
            
            if missing_type == 'x':
                x_numeric[missing_indices] = actual_values
            elif missing_type == 'f':
                f_numeric[missing_indices] = actual_values
            elif missing_type == 'f_double':
                for i, idx in enumerate(missing_indices):
                    f_numeric[idx] = actual_values[i]
            
            # Calculate mean
            sum_fx = sum(x * f for x, f in zip(x_numeric, f_numeric))
            sum_f = sum(f_numeric)
            
            mean = sum_fx / sum_f
            
            # ALWAYS round to 1 decimal place
            return round(mean, 1)
            
        except Exception as e:
            print(f"Error calculating target mean: {e}")
            return None
    
    def generate_distractors(self, correct_answer, num_distractors=3):
        """Generate plausible wrong answers"""
        distractors = set()
        
        # Various distractor strategies
        strategies = [
            lambda x: x + random.uniform(0.1, 2.0),
            lambda x: x - random.uniform(0.1, 2.0),
            lambda x: x * random.uniform(1.1, 1.3),
            lambda x: x * random.uniform(0.7, 0.9),
            lambda x: x + random.uniform(-1.5, 1.5),
            lambda x: round(x) + random.uniform(-0.5, 0.5),  # Common rounding errors
        ]
        
        attempts = 0
        while len(distractors) < num_distractors and attempts < 50:
            strategy = random.choice(strategies)
            distractor = strategy(correct_answer)
            
            # Round to 1 decimal place
            distractor = round(distractor, 1)
            
            # Ensure positive and different from correct answer
            if distractor > 0 and distractor != correct_answer:
                distractors.add(distractor)
            
            attempts += 1
        
        # Fill remaining slots if needed
        while len(distractors) < num_distractors:
            offset = random.choice([0.1, 0.2, 0.3, 0.5, 1.0, 1.5, 2.0, -0.1, -0.2, -0.5])
            distractor = round(correct_answer + offset, 1)
            if distractor > 0 and distractor != correct_answer:
                distractors.add(distractor)
        
        return list(distractors)[:num_distractors]
    
    def create_table_image(self, x_values, frequencies, question_num, table_type="basic", total_freq=None):
        """Create and save table image"""
        fig, ax = plt.subplots(figsize=(12, 4))
        ax.axis('tight')
        ax.axis('off')
        
        # Create table data
        headers = ['X'] + [str(x) for x in x_values]
        freq_row = ['F'] + [str(f) for f in frequencies]
        
        # Add total column ONLY when appropriate
        if table_type == "two_missing_with_total" and total_freq is not None:
            headers.append('Total')
            freq_row.append(str(total_freq))
        elif table_type == "basic" and all(isinstance(f, (int, float)) for f in frequencies):
            # Only add total if no missing frequencies
            total_f = sum(frequencies)
            headers.append('Total')
            freq_row.append(str(total_f))
        
        table_data = [headers, freq_row]
        
        # Create table
        table = ax.table(cellText=table_data, 
                        cellLoc='center',
                        loc='center',
                        bbox=[0, 0, 1, 1])
        
        # Style the table
        table.auto_set_font_size(False)
        table.set_fontsize(11)
        table.scale(1, 2.5)
        
        # Style cells
        for i in range(len(table_data[0])):
            table[(0, i)].set_facecolor('#E6E6FA')  # Header
            table[(0, i)].set_text_props(weight='bold')
            table[(1, i)].set_facecolor('#F0F8FF')  # Data
        
        plt.title(f'Frequency Distribution Table - Question {question_num}', 
                 fontsize=14, fontweight='bold', pad=20)
        
        # Save image
        os.makedirs('table_images', exist_ok=True)
        plt.savefig(f'table_images/question_{question_num}_table.png', 
                   dpi=300, bbox_inches='tight', facecolor='white', edgecolor='none')
        plt.close()
    
    def get_problem_signature(self, x_values, frequencies, var_symbol, target_mean):
        """Create a signature for the problem to avoid duplicates"""
        # Create a simplified signature
        x_str = '_'.join(str(x) for x in x_values)
        f_str = '_'.join(str(f) for f in frequencies)
        return f"{x_str}_{f_str}_{var_symbol}_{target_mean}"
    
    def generate_two_variable_distractors(self, var_symbols, correct_values, num_distractors=3):
        """Generate distractors for two-variable problems"""
        distractors = set()
        
        # Various distractor strategies for two variables
        strategies = [
            # Swap the values
            lambda v1, v2: (v2, v1),
            # Add/subtract small amounts to both
            lambda v1, v2: (v1 + random.uniform(0.1, 1.0), v2 + random.uniform(0.1, 1.0)),
            lambda v1, v2: (v1 - random.uniform(0.1, 1.0), v2 - random.uniform(0.1, 1.0)),
            # Modify one value significantly
            lambda v1, v2: (v1 + random.uniform(1.0, 3.0), v2),
            lambda v1, v2: (v1, v2 + random.uniform(1.0, 3.0)),
            lambda v1, v2: (v1 - random.uniform(1.0, 2.0), v2),
            lambda v1, v2: (v1, v2 - random.uniform(1.0, 2.0)),
            # Scale both values
            lambda v1, v2: (v1 * random.uniform(1.1, 1.4), v2 * random.uniform(1.1, 1.4)),
            lambda v1, v2: (v1 * random.uniform(0.6, 0.9), v2 * random.uniform(0.6, 0.9)),
        ]
        
        attempts = 0
        while len(distractors) < num_distractors and attempts < 50:
            strategy = random.choice(strategies)
            d1, d2 = strategy(correct_values[0], correct_values[1])
            
            # Round to 1 decimal place
            d1, d2 = round(d1, 1), round(d2, 1)
            
            # Ensure positive values and different from correct answer
            if d1 > 0 and d2 > 0:
                distractor_str = f"{var_symbols[0]}={d1}, {var_symbols[1]}={d2}"
                correct_str = f"{var_symbols[0]}={correct_values[0]}, {var_symbols[1]}={correct_values[1]}"
                if distractor_str != correct_str:
                    distractors.add(distractor_str)
            
            attempts += 1
        
        # Fill remaining slots if needed with simple modifications
        while len(distractors) < num_distractors:
            offset1 = random.choice([0.1, 0.2, 0.5, 1.0, -0.1, -0.2, -0.5])
            offset2 = random.choice([0.1, 0.2, 0.5, 1.0, -0.1, -0.2, -0.5])
            d1 = round(correct_values[0] + offset1, 1)
            d2 = round(correct_values[1] + offset2, 1)
            
            if d1 > 0 and d2 > 0:
                distractor_str = f"{var_symbols[0]}={d1}, {var_symbols[1]}={d2}"
                correct_str = f"{var_symbols[0]}={correct_values[0]}, {var_symbols[1]}={correct_values[1]}"
                if distractor_str != correct_str:
                    distractors.add(distractor_str)
        
        return list(distractors)[:num_distractors]
    
    def generate_problem(self, question_num):
        """Generate a complete problem with various patterns"""
        # Choose problem type based on patterns
        problem_types = [
            'single_missing_value',           # Find missing x value
            'single_missing_frequency',       # Find missing frequency
            'two_missing_frequencies',        # Find two missing frequencies (same variable) with total
            'two_different_frequencies',      # Find two different missing frequencies (p and q) with total
            'algebraic_expressions'           # Algebraic expressions with variable
        ]
        
        max_attempts = 20
        for attempt in range(max_attempts):
            try:
                problem_type = random.choice(problem_types)
                
                if problem_type == 'single_missing_value':
                    x_values, frequencies, var_symbol, actual_missing, missing_type, missing_index = self.generate_single_missing_value_table()
                    target_mean = self.calculate_target_mean_from_actual(x_values, frequencies, var_symbol, actual_missing, missing_type, missing_index)
                    
                    # Check for duplicates
                    signature = self.get_problem_signature(x_values, frequencies, var_symbol, target_mean)
                    if signature in self.generated_problems:
                        continue
                    
                    solved_value = self.solve_for_missing_value(x_values, frequencies, var_symbol, 
                                                             (missing_type, missing_index), target_mean)
                    
                    if solved_value is not None and abs(solved_value - actual_missing) < 0.15:
                        self.generated_problems.add(signature)
                        correct_answer = round(actual_missing, 1)
                        template = random.choice(self.question_templates)
                        question_text = template.format(var=var_symbol, mean=target_mean)
                        table_type = "basic"
                        total_freq = None
                        break
                
                elif problem_type == 'single_missing_frequency':
                    x_values, frequencies, var_symbol, actual_missing, missing_type, missing_index = self.generate_single_missing_frequency_table()
                    target_mean = self.calculate_target_mean_from_actual(x_values, frequencies, var_symbol, actual_missing, missing_type, missing_index)
                    
                    # Check for duplicates
                    signature = self.get_problem_signature(x_values, frequencies, var_symbol, target_mean)
                    if signature in self.generated_problems:
                        continue
                    
                    solved_value = self.solve_for_missing_value(x_values, frequencies, var_symbol, 
                                                             (missing_type, missing_index), target_mean)
                    
                    if solved_value is not None and abs(solved_value - actual_missing) < 0.15:
                        self.generated_problems.add(signature)
                        correct_answer = round(actual_missing, 1)
                        template = random.choice(self.question_templates)
                        question_text = template.format(var=var_symbol, mean=target_mean)
                        table_type = "basic_no_total"  # Don't show total when frequency is missing
                        total_freq = None
                        break
                
                elif problem_type == 'two_missing_frequencies':
                    x_values, frequencies, var_symbol, actual_missing, total_frequency, missing_indices = self.generate_two_missing_frequencies_table()
                    target_mean = self.calculate_target_mean_from_actual(x_values, frequencies, var_symbol, [actual_missing, actual_missing], 'f_double', missing_indices)
                    
                    # Check for duplicates
                    signature = self.get_problem_signature(x_values, frequencies, var_symbol, target_mean)
                    if signature in self.generated_problems:
                        continue
                    
                    solved_value = self.solve_for_missing_value(x_values, frequencies, var_symbol, 
                                                             ('f_double', missing_indices), target_mean, total_frequency)
                    
                    if solved_value is not None and abs(solved_value - actual_missing) < 0.15:
                        self.generated_problems.add(signature)
                        correct_answer = round(actual_missing, 1)
                        template = random.choice(self.two_missing_templates)
                        question_text = template.format(var=var_symbol, mean=target_mean, total=total_frequency)
                        table_type = "basic_no_total"
                        total_freq = total_frequency
                        break
                
                elif problem_type == 'two_different_frequencies':
                    x_values, frequencies, var_symbols, actual_missing_values, total_frequency, missing_indices = self.generate_two_different_missing_frequencies_table()
                    target_mean = self.calculate_target_mean_from_actual(x_values, frequencies, var_symbols[0], actual_missing_values, 'f_double', missing_indices)
                    
                    # Check for duplicates
                    signature = self.get_problem_signature(x_values, frequencies, f"{var_symbols[0]}_{var_symbols[1]}", target_mean)
                    if signature in self.generated_problems:
                        continue
                    
                    solved_values = self.solve_two_different_frequencies(x_values, frequencies, var_symbols, 
                                                                    missing_indices, target_mean, total_frequency)
                    
                    if solved_values is not None:
                        self.generated_problems.add(signature)
                        # Format the correct answer as "p=x, q=y"
                        correct_answer = f"{var_symbols[0]}={solved_values[0]}, {var_symbols[1]}={solved_values[1]}"
                        template = f"The mean of the following distribution is {target_mean} and the total frequency is {total_frequency}. Find the values of {var_symbols[0]} and {var_symbols[1]}. (Round your answers to 1 decimal place)"
                        question_text = template
                        table_type = "two_missing_with_total"
                        total_freq = total_frequency
                        # Store the actual values for distractor generation
                        actual_values = solved_values
                        problem_type_final = 'two_different_frequencies'
                        break

                
                elif problem_type == 'algebraic_expressions':
                    x_values, frequencies, var_symbol, coefficients, constants = self.generate_algebraic_expressions_table()
                    
                    # Generate target mean by picking a reasonable variable value
                    target_var_value = round(random.uniform(2.0, 8.0), 1)
                    
                    # Calculate mean with this value
                    sum_fx = sum_f = 0
                    valid = True
                    for x, coeff, const in zip(x_values, coefficients, constants):
                        f_val = coeff * target_var_value + const
                        if f_val <= 0:
                            valid = False
                            break
                        sum_fx += x * f_val
                        sum_f += f_val
                    
                    if not valid:
                        continue
                    
                    target_mean = round(sum_fx / sum_f, 1)  # Force 1 decimal place
                    
                    # Check for duplicates
                    signature = self.get_problem_signature(x_values, frequencies, var_symbol, target_mean)
                    if signature in self.generated_problems:
                        continue
                    
                    solved_value = self.solve_for_missing_value(x_values, frequencies, var_symbol, 
                                                             ('algebraic', coefficients, constants), target_mean)
                    
                    if solved_value is not None and abs(solved_value - target_var_value) < 0.15:
                        self.generated_problems.add(signature)
                        correct_answer = round(target_var_value, 1)
                        template = random.choice(self.question_templates)
                        question_text = template.format(var=var_symbol, mean=target_mean)
                        table_type = "algebraic"
                        total_freq = None
                        break
                        
            except Exception as e:
                print(f"Attempt {attempt + 1} failed: {e}")
                continue
        else:
            # Fallback to simple case
            return self.generate_simple_fallback_problem(question_num)
        
        # Generate distractors
        if 'problem_type_final' in locals() and problem_type_final == 'two_different_frequencies':
            distractors = self.generate_two_variable_distractors(var_symbols, actual_values)
        else:
            distractors = self.generate_distractors(correct_answer)
        
        # Create table image
        self.create_table_image(x_values, frequencies, question_num, table_type, total_freq)
        
        # Shuffle options
        all_options = [correct_answer] + distractors
        random.shuffle(all_options)
        
        return {
            'question': question_text,
            'answer': correct_answer,
            'option_A': all_options[0],
            'option_B': all_options[1],
            'option_C': all_options[2],
            'option_D': all_options[3],
        }
    
    def generate_simple_fallback_problem(self, question_num):
        """Generate a simple fallback problem when complex generation fails"""
        # Simple case: one missing frequency
        x_values = [1, 2, 3, 4, 5]
        frequencies = [3, 7, 'p', 6, 4]
        var_symbol = 'p'
        
        # Let's say p = 5, then mean = (1*3 + 2*7 + 3*5 + 4*6 + 5*4) / (3+7+5+6+4) = 3.0
        actual_p = 5
        target_mean = 3.0
        
        # Question
        question_text = f"If the mean of the following distribution is {target_mean}, find the value of {var_symbol}. (Round your answer to 1 decimal place)"
        
        # Generate distractors
        distractors = self.generate_distractors(actual_p)
        
        # Create table image
        self.create_table_image(x_values, frequencies, question_num, "basic_no_total")
        
        # Shuffle options
        all_options = [actual_p] + distractors
        random.shuffle(all_options)
        
        return {
            'question': question_text,
            'answer': actual_p,
            'option_A': all_options[0],
            'option_B': all_options[1],
            'option_C': all_options[2],
            'option_D': all_options[3],
        }
    
    def generate_questions(self, num_questions):
        """Generate specified number of questions"""
        questions_data = []
        
        print(f"Generating {num_questions} statistics problems...")
        print("Creating table images...")
        
        for i in range(1, num_questions + 1):
            print(f"  Generating question {i}/{num_questions}")
            problem = self.generate_problem(i)
            questions_data.append(problem)
        
        # Save to CSV
        df = pd.DataFrame(questions_data)
        filename = f'enhanced_statistics_problems_{num_questions}_questions.csv'
        df.to_csv(filename, index=False)
        
        print(f"\n✅ Successfully generated {num_questions} questions!")
        print(f"📄 Questions saved to: {filename}")
        print(f"🖼️  Table images saved to: table_images/ folder")
        
        # Show sample question
        sample = questions_data[0]
        print(f"\nSample question:")
        print(f"Q: {sample['question']}")
        print(f"A) {sample['option_A']}")
        print(f"B) {sample['option_B']}")
        print(f"C) {sample['option_C']}")
        print(f"D) {sample['option_D']}")
        print(f"Correct answer: {sample['answer']}")
        
        return questions_data
---

${moreRefs ? "\n---\n## 📄 Additional Reference Scripts:\n\n" + moreRefs : ""}

---

## 🧠 New Topic to Build Script For

Topic: ${topic || "[Insert topic name here]"}

## Types of question:
These are types of questions that I need -
${types || "[Insert]"}

### 🔹 Sample Questions

Below are examples of the types of questions I want the new script to generate. Please infer everything from these samples — question pattern, logic, answer format, and distractor style.

\`\`\`
${examples || "[Paste sample questions here]"}
\`\`\`

---
## Image Generation Logic
def generateImage(qNum, data):
    df = pd.DataFrame(data)
    cname, fname = [*data]
    df_hz = pd.DataFrame([df[cname], df[fname]], index=[cname, fname])
    fig, ax = plt.subplots(figsize=(10, 2.5))
    table = ax.table(cellText=df_hz.values, rowLabels=df_hz.index, cellLoc='center', loc='center')
    table.auto_set_font_size(False)
    table.set_fontsize(15)
    table.scale(1.5, 5)
    ax.axis('off')
    output_path = f"Q{qNum}.jpg"
    plt.savefig(output_path, pad_inches=0.3, bbox_inches='tight')

## ✨ Option & Distractor Logic

Each question must have:
- 1 correct answer
- 3 distractors based on common errors or misconceptions students might make in this topic

Examples of distractors:
- Mistake in formula application
- Sign error
- Wrong factor
- Arithmetic error
- Choosing extraneous roots

---

## ✅ Final Requirements
* Important: Generate working Python script only.
* Important: Python script should ask for number of questions to generate
* Script must generate questions similar to the samples provided
* Output should be saved to a CSV file with columns:
  \`question\`, \`answer\`, \`option_A\`, \`option_B\`, \`option_C\`, \`option_D\`
If its Image based question then include column 'question_type' with value 'image' in csv file.
* All expressions formatted in ASCII math style
* Script must be modular, readable, and include comments
* Take input number of questions of each type that needs to be made.
* If the Topic is Image based question, then generate Image as well for each question.
* Print all the questions generated in the console as well.

${extraNotes ? "\n---\n## 📝 Extra Instructions:\n\n" + extraNotes : ""}

---

Now, implement this new script based on the above logic and my original reference script.
      `;

    setOutput(prompt.trim());
  };

  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(output);
      setCopyStatus("Copied!");
      setTimeout(() => setCopyStatus("Copy Prompt"), 2000);
    } catch (err) {
      console.error("Failed to copy: ", err);
      setCopyStatus("Copy Failed");
      setTimeout(() => setCopyStatus("Copy Prompt"), 2000);
    }
  };
  if (!isAuthorized) {
    return (
      <div className="container-fluid mt-5">
        <h2 style={{ marginTop: "5%" }}>
          You are unauthorized to access this page
        </h2>
      </div>
    );
  }
  return (
    <>
      {!isDesktopDevice && (
          <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          margin: "0.5rem 0.75rem 1.25rem 0.75rem",
        }}>
            <div
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                width: "36px",
                height: "36px",
                borderRadius: "18px",
                background: "#EAF1F7",
              }}
            >
              <Hemberger />
            </div>
            <Link to="/profile">
              <UserIcon />
            </Link>
            <Sidebar isOpen={isSidebarOpen} onClose={closeSidebar} />
          </div>
      )}
      <div className={styles.container} style={{ marginTop: isDesktopDevice ? "100px" : "0px" }}>
        {/* Dark mode toggle */}
        <div className={styles.themeToggle} style={{ marginTop: !isDesktopDevice ? "15px":"50px" }}>
          <input
            type="checkbox"
            id="themeSwitch"
            checked={isDark}
            onChange={toggleTheme}
          />
          <label htmlFor="themeSwitch"></label>
        </div>

        <h1>🧠 Gemini Prompt Generator for Math Scripts</h1>

        <label>1. Topic Name:</label>
        <input
          type="text"
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          placeholder="e.g. Quadratic Equations"
        />

        <label>2. Types of Questions:</label>
        <textarea
          rows="3"
          value={types}
          onChange={(e) => setTypes(e.target.value)}
          placeholder="- Type 1: Solve using factorization\n- Type 2: Solve using quadratic formula"
        />

        <label>3. Sample Questions:</label>
        <textarea
          rows="6"
          value={examples}
          onChange={(e) => setExamples(e.target.value)}
          placeholder="Factorize: x^2 - 5x + 6\nSolve: x^2 + 4x + 4 = 0"
        />

        <details>
          <summary>⚙️ Advanced Options</summary>

          <label>5. Additional Reference Scripts (optional):</label>
          <textarea
            rows="6"
            value={moreRefs}
            onChange={(e) => setMoreRefs(e.target.value)}
            placeholder="# Paste additional sample/reference scripts..."
          />

          <label>6. Extra Instructions (optional):</label>
          <textarea
            rows="4"
            value={extraNotes}
            onChange={(e) => setExtraNotes(e.target.value)}
            placeholder="Anything else Gemini should consider..."
          />
        </details>

        <button onClick={generatePrompt}>Generate Prompt</button>
        <button
          className={copyStatus === "Copied!" ? styles.copied : ""}
          onClick={copyPrompt}
        >
          {copyStatus}
        </button>

        <h2>📋 Final Prompt:</h2>
        <div className={styles.output}>{output}</div>
      </div>
      <GeminiChat prompt={output} />
    </>
  );
};

export default GenerateQuestions;
